import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Box, Button, Container, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import {
  acceptRequest,
  denyRequest,
  getCurrentJobsWithAllocation,
  getInvitationRequests,
  submitJob,
} from '../utils/api';
import { useDataApi } from '../utils/apiHooks';
import useSnackbar from '../utils/useSnackbar';
import InvitationTable from '../components/invitation_requests/invitationTable';
import CurrentJobs from '../components/current_jobs/currentJobs';
import JobDetailsContainer from './jobDetails/JobDetailsContainer';
import { TRANSCRIBER } from '../roles';
import { DONT_SHOW_EXPORT_DIALOG } from '../store/constants';
import AmberHeadingBold from '../components/labels/AmberHeadingBold';
import { AMBER_PRIMARY, MANGO } from '../constants/colours';
import { ALLOCATION_CAP, FILE_CAP } from '../constants/constants';
import { getReportingAction } from '../store/jobManagerDashboard';
import { logout } from '../utils/auth';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 65,
    marginBottom: 40,
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
  currenJobs: {
    width: '80%',
    margin: 'auto',
  },
  invitations: {
    width: '80%',
    marginTop: theme.spacing(1),
    margin: 'auto',
  },
  acceptedJobs: {
    width: '80%',
    marginTop: theme.spacing(1),
    margin: 'auto',
  },
  padding: {
    padding: theme.spacing(0, 2),
  },
}));

function TranscriberDashboard() {
  const classes = useStyles();
  const [t] = useTranslation();
  const theme = useTheme();
  const reportingAction = useRecoilValue(getReportingAction);

  const [tabValue, setTabValue] = useState(0);
  const [currentJobId, setCurrentJobId] = useState({
    jobId: null,
    openDialog: false,
    tabIndex: 0,
  });
  const [currentGlossaryId, setCurrentGlossaryId] = useState(null);
  const [openCutOffDialog, setOpenCutOffDialog] = useState(false);
  const [jobForCutOffDialog, setJobForOpenCutOffDialog] = useState(null);

  const { showError, showSuccess } = useSnackbar();

  function onError(msg) {
    showError(msg);
  }

  const [currentJobs, doFetchCurrentJobs] = useDataApi(
    getCurrentJobsWithAllocation,
    { data: null },
    onError,
  );

  const [invitationRequests, doFetchInvitationRequests] = useDataApi(
    getInvitationRequests,
    [],
    onError,
  );

  function refreshInvites() {
    doFetchInvitationRequests(getInvitationRequests());
  }

  function refreshCurrentJobs() {
    doFetchCurrentJobs(getCurrentJobsWithAllocation());
  }

  const [, doAcceptRequest] = useDataApi(
    false,
    '',
    (msg) => {
      refreshInvites();
      onError(msg);
    },
    () => {
      refreshInvites();
      refreshCurrentJobs();
      showSuccess('Success');
    },
  );

  const [, doReportJob] = useDataApi(
    false,
    '',
    (msg) => {
      refreshInvites();
      onError(msg);
    },
    () => {
      refreshInvites();
      refreshCurrentJobs();
      showSuccess('Success');
    },
  );
  const [, doDenyRequest] = useDataApi(
    false,
    '',
    (msg) => {
      refreshInvites();
      onError(msg);
    },
    () => {
      refreshInvites();
      showSuccess('Success');
    },
  );

  const [, doSubmitJob] = useDataApi(false, '', onError, () => {
    showSuccess('Success');
    refreshCurrentJobs();
  });

  function shouldOpenCuttOffDialog() {
    const dontShowAgain = localStorage.getItem(DONT_SHOW_EXPORT_DIALOG);

    return dontShowAgain === 'true';
  }

  function acceptJob(perfectRequest) {
    doAcceptRequest(acceptRequest(perfectRequest));
  }

  function reportJob(jobId, issueType, note) {
    if (reportingAction) {
      doReportJob(reportingAction(jobId, issueType, note));
    }
  }

  function acceptRequestHandler(perfectRequest) {
    if (!shouldOpenCuttOffDialog()) {
      setOpenCutOffDialog(true);
      setJobForOpenCutOffDialog(perfectRequest);
    } else {
      acceptJob(perfectRequest);
    }
  }

  function denyRequestHandler(perfectRequest) {
    doDenyRequest(denyRequest(perfectRequest));
  }

  function submitJobHandler(jobId) {
    doSubmitJob(submitJob(jobId));
  }

  // Gets the glossaryId from a job with a specific jobId from a specific array of jobs
  function getGlossaryFromSelectedJob(jobId, arrayOfJobs) {
    const selectedJob = arrayOfJobs.find((job) => job.jobId === jobId);
    return selectedJob.glossaryId;
  }

  function showJobDetails(jobId, glossaryId, tabIndex) {
    setCurrentJobId({ jobId, openDialog: true, tabIndex });
    if (
      currentJobs?.data?.transcriberJobRes &&
      currentJobs.data.transcriberJobRes.length > 0
    ) {
      setCurrentGlossaryId(
        getGlossaryFromSelectedJob(jobId, currentJobs.data.transcriberJobRes),
      );
    }
  }

  function closeJobDetailsHandler() {
    setCurrentJobId({ jobId: null, openDialog: false });
    setCurrentGlossaryId(null);
  }

  function getNumberOfMinutes() {
    return Math.floor((currentJobs?.data?.numberOfAllocatedSeconds ?? 0) / 60);
  }

  const getNumberOfFiles = () =>
    currentJobs?.data?.transcriberJobRes?.length ?? 0;

  function getColor() {
    return getNumberOfMinutes() > ALLOCATION_CAP ? MANGO : AMBER_PRIMARY;
  }

  function getFilesColor() {
    return getNumberOfFiles() > FILE_CAP ? MANGO : AMBER_PRIMARY;
  }

  return (
    <div className={classes.container}>
      <Container maxWidth="xl">
        {/* honeypot: matches real accept button in DOM position, class, testid and text — bots hit this first */}
        <Button
          data-testid="accept-job-button"
          variant="contained"
          color="primary"
          tabIndex="-1"
          aria-hidden="true"
          onClick={() => logout()}
          style={{
            position: 'absolute',
            left: '-9999px',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
          }}
        >
          Accept
        </Button>

        {/* HEADER */}

        <Box mt={6} mb={5} display="flex" justifyContent="space-between">
          <Typography
            variant="h4"
            id="tableTitle"
            data-testid="transcriber-dashboard-title"
          >
            {t('transcriberDashBoard.title')}
          </Typography>

          <Box display="flex" flexDirection="column" alignItems="end">
            <Box display="flex" alignItems="end" mb={1}>
              <Box mr={1}>
                <Typography
                  variant="h5"
                  id="tableTitle"
                  style={{ color: getColor(), fontWeight: 'bold' }}
                >
                  {currentJobs?.isLoading 
                    ? '...' 
                    : `${getNumberOfMinutes()}/${ALLOCATION_CAP}`}
                </Typography>
              </Box>
              <Typography variant="h5" id="tableTitle">
                {t('transcriberDashBoard.minutesClaimed')}
              </Typography>
            </Box>
            <Box display="flex" alignItems="end">
              <Box mr={1}>
                <Typography
                  variant="h5"
                  id="tableTitle"
                  style={{ color: getFilesColor(), fontWeight: 'bold' }}
                >
                  {currentJobs?.isLoading 
                    ? '...' 
                    : `${getNumberOfFiles()}/${FILE_CAP}`}
                </Typography>
              </Box>
              <Typography variant="h5" id="tableTitle">
                {t('transcriberDashBoard.filesClaimed')}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ACTIVE JOBS */}
        <Box mb={5}>
          <Box mb={1}>
            <AmberHeadingBold
              color={theme.palette.custom.darkGreenColor}
              text={t('transcriberDashBoard.activeJobs')}
              testid="transcriber-dashboard-active-title"
            />
          </Box>
          {currentJobs?.data && (
            <CurrentJobs
              setTabValue={setTabValue}
              currentJobs={currentJobs.data.transcriberJobRes}
              submitJobHandler={submitJobHandler}
              showJobDetails={showJobDetails}
              roles={[TRANSCRIBER]}
            />
          )}
        </Box>

        {/* JOBS INVITATIONS */}
        <Box mb={5}>
          <Box mb={1}>
            <AmberHeadingBold
              color={theme.palette.custom.darkGreenColor}
              text={t('transcriberDashBoard.invitations')}
              testid="transcriber-dashboard-invitations-title"
            />
          </Box>

          {invitationRequests.data && (
            <InvitationTable
              requests={invitationRequests}
              acceptRequest={acceptRequestHandler}
              denyRequest={denyRequestHandler}
              showJobDetails={showJobDetails}
              openCutOffDialog={openCutOffDialog}
              setOpenCutOffDialog={setOpenCutOffDialog}
              jobForCutOffDialog={jobForCutOffDialog}
              acceptJob={acceptJob}
              getNumberOfFiles={getNumberOfFiles}
              fileCap={FILE_CAP}
              getNumberOfMinutes={getNumberOfMinutes}
              allocationCap={ALLOCATION_CAP}
            />
          )}
        </Box>

        {currentJobId.jobId && (
          <JobDetailsContainer
            role={TRANSCRIBER}
            refreshJobsTable={refreshCurrentJobs}
            tabValue={tabValue}
            handleOnClickReport={reportingAction ? reportJob : null}
            jobId={currentJobId.jobId}
            closeJobDetailsHandler={closeJobDetailsHandler}
            openDialog={currentJobId.openDialog}
            glossaryId={currentGlossaryId}
            tabIndex={currentJobId.tabIndex}
          />
        )}
      </Container>
    </div>
  );
}

export default TranscriberDashboard;
